import { z } from 'zod';
import { IdSchema, MetadataOnlyIntentSchema, PromptAnswerSchema } from './horde-event-schema-helpers';
import { HordeGameFeaturesPatchSchema } from './horde-game-features';
import {
    ActionInstanceSchema,
    HordeInstructionSchema,
    HordeSessionUnitSchema,
    MaterializedRandomResultSchema,
    PublicPlayerStateSchema,
    RecordedTableFactSchema,
    SpawnCompositionNodeSchema,
    SpawnSourceSchema,
} from './horde-session';

export const HordeIntentPayloadSchemasC = {
    RESOLVE_DATASHEET_ABILITY: PromptAnswerSchema.extend({
        abilityId: IdSchema,
        unitId: IdSchema,
    }),
    RESOLVE_DEFERRED_INGRESS: MetadataOnlyIntentSchema.extend({
        fallbackToSpawnZone: z.boolean(),
        unitId: IdSchema,
    }),
    RESOLVE_EFFECT_PROMPT: PromptAnswerSchema.extend({
        effectId: IdSchema,
    }),
    RESOLVE_RESUPPLY_PURCHASE: PromptAnswerSchema.extend({
        playerId: IdSchema,
        purchaseRecordId: IdSchema,
    }),
    RESOLVE_SECONDARY: MetadataOnlyIntentSchema.extend({
        result: z.enum(['successful', 'failed']),
        secondaryCardId: IdSchema,
    }),
    RESPAWN_SET_ASIDE_UNIT: MetadataOnlyIntentSchema.extend({
        originalUnitId: IdSchema,
        unit: HordeSessionUnitSchema,
    }),
    REVEAL_SECRET_OBJECTIVE: MetadataOnlyIntentSchema.extend({
        cardId: IdSchema,
        playerId: IdSchema,
        vaultRevision: z.number().int().nonnegative(),
    }),
    REVERT_ACCEPTED_EVENT: MetadataOnlyIntentSchema.extend({
        compensation: z.object({
            factsToAdd: z.array(RecordedTableFactSchema),
            resourceAdjustments: z.array(
                z.object({
                    amount: z.number().int(),
                    playerId: IdSchema,
                    resource: z.enum(['SP', 'CP']),
                })
            ),
        }),
        reason: z.string().min(1),
        targetEventId: IdSchema,
    }),
    ROLL_DOWN: MetadataOnlyIntentSchema.extend({
        node: SpawnCompositionNodeSchema,
        replacedNodeId: IdSchema,
    }),
    SAVE_SECONDARY_REWARD_CARD: MetadataOnlyIntentSchema.extend({
        playerId: IdSchema,
        rewardCardId: IdSchema,
    }),
    SELECT_DEPLOYMENT: MetadataOnlyIntentSchema.extend({
        deploymentId: IdSchema,
    }),
    SELECT_MOVEMENT_TARGET: MetadataOnlyIntentSchema.extend({
        instruction: HordeInstructionSchema,
    }),
    SELECT_PRIMARY_FACTION_RULE: MetadataOnlyIntentSchema.extend({
        factionId: IdSchema,
        ruleId: IdSchema,
    }),
    SELECT_RANDOM_TIED_TARGET: MetadataOnlyIntentSchema.extend({
        randomResult: MaterializedRandomResultSchema,
        targetId: IdSchema,
        unitId: IdSchema,
    }),
    SELECT_ROUND_MODE: MetadataOnlyIntentSchema.extend({
        roundMode: z.enum(['fiveRounds', 'infinite']),
    }),
    SELECT_SPAWN_UNIT: MetadataOnlyIntentSchema.extend({
        node: SpawnCompositionNodeSchema,
        unitId: IdSchema,
    }),
    SELECT_SPAWN_ZONE: MetadataOnlyIntentSchema.extend({
        source: SpawnSourceSchema,
        zoneId: IdSchema,
    }),
    SET_ASIDE_GSC_UNIT: MetadataOnlyIntentSchema.extend({
        availableRound: z.number().int().positive(),
        randomResult: MaterializedRandomResultSchema,
        unitId: IdSchema,
    }),
    SET_GAME_FEATURES: MetadataOnlyIntentSchema.extend({
        patch: HordeGameFeaturesPatchSchema,
    }),
    SET_GAME_MASTER: MetadataOnlyIntentSchema.extend({
        gameMasterUserId: IdSchema.nullable(),
    }),
    SET_GAME_SIZE: MetadataOnlyIntentSchema.extend({
        gameSize: z.union([z.literal(1000), z.literal(2000)]),
    }),
    SET_HORDE_FACTIONS: MetadataOnlyIntentSchema.extend({
        /** Durable reference to a replicated custom bracket; generated rows never travel in game intents. */
        customSpawnBracketId: IdSchema.nullable().optional(),
        factionIds: z.array(IdSchema).min(1),
    }),
    SET_PLAYERS: MetadataOnlyIntentSchema.extend({
        players: z.array(PublicPlayerStateSchema).min(1).max(4),
    }),
    SPEND_CP: MetadataOnlyIntentSchema.extend({
        amount: z.number().int().positive(),
        playerId: IdSchema,
        sourceId: IdSchema,
    }),
    SPEND_SP: MetadataOnlyIntentSchema.extend({
        amount: z.number().int().positive(),
        playerId: IdSchema,
        sourceId: IdSchema,
    }),
    START_MISSION_ACTION: MetadataOnlyIntentSchema.extend({
        action: ActionInstanceSchema,
    }),
    START_ROUND_STEP: MetadataOnlyIntentSchema,
    TRANSFER_SP: MetadataOnlyIntentSchema.extend({
        amount: z.number().int().positive(),
        fromPlayerId: IdSchema,
        toPlayerId: IdSchema,
    }),
    UPDATE_MARKER_TALLY: MetadataOnlyIntentSchema.extend({
        amount: z.number(),
        markerId: IdSchema,
    }),
} satisfies Record<string, z.ZodType>;
